import { useToggleSet } from "@frontend/hooks/use-toggle-set"
import { getDraggedSeriesId } from "@frontend/pages/case-workspace/lib/viewer-dnd"
import {
  buildVectorStyle,
  findGroupForLabel,
  resolveVectorLabelKey
} from "@frontend/pages/case-workspace/pathology/components/module-schema"
import {
  applyOpacityToStyle,
  clampOpacity01,
  getEffectiveOpacity,
  restoreVisibleOpacity
} from "@frontend/pages/case-workspace/pathology/lib/pathology-vector-opacity"
import type {
  LabelRasterTool,
  ProjectModuleItem,
  VectorGroup,
  VectorLabel,
  VectorTool,
  WsiSource
} from "@frontend/pages/case-workspace/pathology/types"
import type { VectorAnnotationState } from "@shared/lib/history-types"
import {
  type AnnotationSelectedEvent,
  type ImageSourceType,
  type PointerMoveEvent,
  TileClient,
  TileViewer,
  type ViewportChangeEvent
} from "@virdx/wsi-viewer"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export interface ViewerStatusInfo {
  pointerImageX: number | null
  pointerImageY: number | null
  zoom: number
  level: number
  imageWidth: number
  imageHeight: number
}

interface UsePathologyViewerParams {
  caseId?: string
  wsiSources?: WsiSource[]
  activeSeriesId: string | null
  activeModule: ProjectModuleItem | null
  activeVectorLabelKey: string | null
  activeVectorTool: VectorTool | null
  vectorEraserRadiusPx: number
  vectorBrushRadiusPx: number
  activeVectorLabels: VectorLabel[]
  activeVectorGroups: VectorGroup[]
  vectors: VectorAnnotationState[]
  replaceVectorsForModule: (params: {
    projectModuleId: string
    seriesId: string
    vectors: VectorAnnotationState[]
  }) => void
  getAuthHeaders: () => Promise<Record<string, string> | undefined>
  isActiveModuleEditable: boolean
  isSeriesDragging: boolean
  onSeriesDrop?: (seriesId: string) => void
  onVectorEraserRadiusChange?: (radiusPx: number) => void
  onVectorBrushRadiusChange?: (radiusPx: number) => void
  onVectorToolChange?: (tool: VectorTool | null) => void
  /** Active in-place label mask tool (label_raster modules), for cursor styling. */
  labelRasterTool?: LabelRasterTool | null
  /**
   * The active vector module draws inside edit regions that are not known yet
   * (request pending / failed): vector tools stay off rather than falling open.
   */
  vectorEditingBlocked?: boolean
}

export function usePathologyViewer({
  caseId,
  wsiSources,
  activeSeriesId,
  activeModule,
  activeVectorLabelKey,
  activeVectorTool,
  vectorEraserRadiusPx,
  vectorBrushRadiusPx,
  activeVectorLabels,
  activeVectorGroups,
  vectors,
  replaceVectorsForModule,
  getAuthHeaders,
  isActiveModuleEditable,
  isSeriesDragging,
  onSeriesDrop,
  onVectorEraserRadiusChange,
  onVectorBrushRadiusChange,
  onVectorToolChange,
  labelRasterTool = null,
  vectorEditingBlocked = false
}: UsePathologyViewerParams) {
  /** Increments each time a base image finishes loading into a (re)created viewer. */
  const [viewerEpoch, setViewerEpoch] = useState(0)
  const [viewerStatus, setViewerStatus] = useState<ViewerStatusInfo | null>(null)
  const statusRef = useRef<{ pxX: number | null; pxY: number | null; zoom: number; level: number }>({
    pxX: null,
    pxY: null,
    zoom: 1,
    level: 0
  })
  const statusRafRef = useRef(0)

  const flushStatus = useCallback(() => {
    statusRafRef.current = 0
    const s = statusRef.current
    setViewerStatus((prev) =>
      prev ? { ...prev, pointerImageX: s.pxX, pointerImageY: s.pxY, zoom: s.zoom, level: s.level } : null
    )
  }, [])

  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null)
  const [isViewerDropTarget, setIsViewerDropTarget] = useState(false)
  const [lastVisibleOpacityById, setLastVisibleOpacityById] = useState<Record<string, number>>({})
  const [vectorOpacityDraftById, setVectorOpacityDraftById] = useState<Record<string, number>>({})

  const activeVectorLabelByKey = useMemo(
    () => new Map(activeVectorLabels.map((label) => [label.key, label])),
    [activeVectorLabels]
  )

  const activeModuleVectors = useMemo(() => {
    if (!activeModule || activeModule.template_type !== "vector") return []
    if (!activeSeriesId) return []

    return vectors
      .filter((vector) => vector.projectModuleId === activeModule.id && vector.seriesId === activeSeriesId)
      .sort((left, right) => {
        const timeDelta = new Date(right.annotation.createdAt).getTime() - new Date(left.annotation.createdAt).getTime()
        if (timeDelta !== 0) return timeDelta
        return left.id.localeCompare(right.id)
      })
  }, [activeModule, activeSeriesId, vectors])

  useEffect(() => {
    setVectorOpacityDraftById((curr) => {
      const knownIds = new Set(vectors.map((vector) => vector.id))
      let changed = false
      const next: Record<string, number> = {}
      for (const [id, value] of Object.entries(curr)) {
        if (!knownIds.has(id)) {
          changed = true
          continue
        }
        next[id] = value
      }
      return changed ? next : curr
    })
  }, [vectors])

  const viewerContainerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const viewerRef = useRef<TileViewer | null>(null)
  const isApplyingGroupVisibilityRef = useRef(false)
  const isSyncingFromViewerRef = useRef(false)

  const applyVectorOpacity = useCallback((vector: VectorAnnotationState, opacity: number) => {
    const viewer = viewerRef.current
    if (!viewer) return

    const nextOpacity = clampOpacity01(opacity)
    viewer.updateAnnotationStyle(vector.id, applyOpacityToStyle(vector.annotation.style, nextOpacity))

    if (nextOpacity > 0) {
      setLastVisibleOpacityById((curr) => ({
        ...curr,
        [vector.id]: nextOpacity
      }))
    }
  }, [])

  const handleVectorOpacityChange = useCallback(
    (vector: VectorAnnotationState, opacityPercent: number) => {
      if (!isActiveModuleEditable) return
      setVectorOpacityDraftById((curr) => ({
        ...curr,
        [vector.id]: Math.round(opacityPercent)
      }))
    },
    [isActiveModuleEditable]
  )

  const handleVectorOpacityCommit = useCallback(
    (vector: VectorAnnotationState, opacityPercent: number) => {
      if (!isActiveModuleEditable) return
      setVectorOpacityDraftById((curr) => {
        if (!(vector.id in curr)) return curr
        const clone = { ...curr }
        delete clone[vector.id]
        return clone
      })
      applyVectorOpacity(vector, opacityPercent / 100)
    },
    [applyVectorOpacity, isActiveModuleEditable]
  )

  const handleVectorVisibilityToggle = useCallback(
    (vector: VectorAnnotationState, currentOpacityPercent?: number) => {
      if (!isActiveModuleEditable) return
      const currentOpacity = clampOpacity01(
        (currentOpacityPercent ?? Math.round(getEffectiveOpacity(vector.annotation.style) * 100)) / 100
      )
      setVectorOpacityDraftById((curr) => {
        if (!(vector.id in curr)) return curr
        const clone = { ...curr }
        delete clone[vector.id]
        return clone
      })
      if (currentOpacity > 0) {
        setLastVisibleOpacityById((curr) => ({
          ...curr,
          [vector.id]: currentOpacity
        }))
        applyVectorOpacity(vector, 0)
        return
      }

      const restored = restoreVisibleOpacity(lastVisibleOpacityById[vector.id])
      applyVectorOpacity(vector, restored)
    },
    [applyVectorOpacity, isActiveModuleEditable, lastVisibleOpacityById]
  )

  const handleSelectAnnotation = useCallback((id: string | null) => {
    viewerRef.current?.selectAnnotation(id)
  }, [])

  const handleVectorDelete = useCallback(
    (vectorId: string) => {
      if (!isActiveModuleEditable) return
      const viewer = viewerRef.current
      if (!viewer) return
      setVectorOpacityDraftById((curr) => {
        if (!(vectorId in curr)) return curr
        const clone = { ...curr }
        delete clone[vectorId]
        return clone
      })
      viewer.removeAnnotation(vectorId)
    },
    [isActiveModuleEditable]
  )

  useEffect(() => {
    const container = viewerContainerRef.current
    if (!container) return

    const handleDragEnter = (event: DragEvent) => {
      if (!isSeriesDragging) return
      event.preventDefault()
      event.stopPropagation()
      setIsViewerDropTarget(true)
    }

    const handleDragOver = (event: DragEvent) => {
      if (!isSeriesDragging) return
      event.preventDefault()
      event.stopPropagation()
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy"
      }
      setIsViewerDropTarget(true)
    }

    const handleDragLeave = (event: DragEvent) => {
      if (!isSeriesDragging) return
      event.preventDefault()
      event.stopPropagation()
      const currentTarget = event.currentTarget as HTMLElement | null
      const nextTarget = event.relatedTarget as Node | null
      if (!currentTarget || (nextTarget && currentTarget.contains(nextTarget))) {
        return
      }
      setIsViewerDropTarget(false)
    }

    const handleDrop = (event: DragEvent) => {
      if (!isSeriesDragging) return
      event.preventDefault()
      event.stopPropagation()
      setIsViewerDropTarget(false)
      if (!(event.dataTransfer && onSeriesDrop)) return
      const seriesId = getDraggedSeriesId(event.dataTransfer)
      if (!seriesId) return
      onSeriesDrop(seriesId)
    }

    container.addEventListener("dragenter", handleDragEnter)
    container.addEventListener("dragover", handleDragOver)
    container.addEventListener("dragleave", handleDragLeave)
    container.addEventListener("drop", handleDrop)

    return () => {
      container.removeEventListener("dragenter", handleDragEnter)
      container.removeEventListener("dragover", handleDragOver)
      container.removeEventListener("dragleave", handleDragLeave)
      container.removeEventListener("drop", handleDrop)
    }
  }, [isSeriesDragging, onSeriesDrop])

  useEffect(() => {
    if (isSeriesDragging) return
    setIsViewerDropTarget(false)
  }, [isSeriesDragging])

  const activeSource = useMemo(
    () => wsiSources?.find((s) => s.id === activeSeriesId) ?? null,
    [activeSeriesId, wsiSources]
  )

  const [hiddenGroupKeys, handleToggleGroupVisibility, clearHiddenGroupKeys] = useToggleSet<string>()

  // Reset hidden groups when module changes
  useEffect(() => {
    clearHiddenGroupKeys()
  }, [clearHiddenGroupKeys])

  // Apply/restore opacity when hiddenGroupKeys changes
  const prevHiddenGroupKeysRef = useRef<ReadonlySet<string>>(hiddenGroupKeys)
  useEffect(() => {
    const viewer = viewerRef.current
    if (!(viewer && activeSeriesId)) return

    const prev = prevHiddenGroupKeysRef.current
    // Skip when nothing changed — activeModuleVectors may have a new reference
    // but visibility only needs updating when hiddenGroupKeys actually changes.
    if (prev === hiddenGroupKeys) return
    prevHiddenGroupKeysRef.current = hiddenGroupKeys

    isApplyingGroupVisibilityRef.current = true
    for (const vector of activeModuleVectors) {
      const group = vector.labelName ? findGroupForLabel(activeVectorGroups, vector.labelName) : null
      if (!group) continue

      const wasHidden = prev.has(group.key)
      const isHidden = hiddenGroupKeys.has(group.key)
      if (wasHidden === isHidden) continue

      if (isHidden) {
        viewer.updateAnnotationStyle(vector.id, {
          ...vector.annotation.style,
          strokeOpacity: 0,
          fillOpacity: 0
        })
      } else {
        viewer.updateAnnotationStyle(vector.id, vector.annotation.style)
      }
    }
    isApplyingGroupVisibilityRef.current = false
  }, [hiddenGroupKeys, activeModuleVectors, activeVectorGroups, activeSeriesId])

  const activeVectorModuleRef = useRef<ProjectModuleItem | null>(null)
  const activeVectorLabelsRef = useRef<VectorLabel[]>([])
  const activeSeriesIdRef = useRef<string | null>(null)
  const activeVectorLabelKeyRef = useRef<string | null>(null)
  const activeVectorToolRef = useRef<VectorTool | null>(activeVectorTool)
  const isEraseGestureActiveRef = useRef(false)
  const hasEraseGestureChangesRef = useRef(false)
  const onVectorEraserRadiusChangeRef = useRef(onVectorEraserRadiusChange)
  const onVectorBrushRadiusChangeRef = useRef(onVectorBrushRadiusChange)
  const onVectorToolChangeRef = useRef(onVectorToolChange)
  useEffect(() => {
    onVectorEraserRadiusChangeRef.current = onVectorEraserRadiusChange
    onVectorBrushRadiusChangeRef.current = onVectorBrushRadiusChange
    onVectorToolChangeRef.current = onVectorToolChange
  }, [onVectorEraserRadiusChange, onVectorBrushRadiusChange, onVectorToolChange])

  useEffect(() => {
    const isVectorModule = activeModule?.template_type === "vector"
    activeVectorModuleRef.current = isVectorModule ? activeModule : null
    activeVectorLabelsRef.current = isVectorModule ? activeVectorLabels : []
    activeSeriesIdRef.current = activeSeriesId
    activeVectorLabelKeyRef.current = activeVectorLabelKey
  }, [activeModule, activeSeriesId, activeVectorLabelKey, activeVectorLabels])

  useEffect(() => {
    activeVectorToolRef.current = activeVectorTool
  }, [activeVectorTool])

  const syncVectorsFromViewer = useCallback(() => {
    const viewer = viewerRef.current
    const module = activeVectorModuleRef.current
    const seriesId = activeSeriesIdRef.current
    const labels = activeVectorLabelsRef.current
    const fallbackLabelKey = activeVectorLabelKeyRef.current

    if (!(viewer && module && seriesId)) return

    const annotations = viewer.getAnnotations(seriesId)
    const nextVectors = annotations.map((annotation) => {
      const labelKey = resolveVectorLabelKey(annotation, labels, fallbackLabelKey)
      const labelIndex = labelKey ? labels.findIndex((item) => item.key === labelKey) : -1

      const next: VectorAnnotationState = {
        id: annotation.id,
        seriesId,
        projectModuleId: module.id,
        labelName: labelKey,
        labelValue: labelIndex >= 0 ? labelIndex + 1 : null,
        annotation: {
          ...annotation,
          label: labelKey ?? annotation.label
        }
      }

      return next
    })

    isSyncingFromViewerRef.current = true
    replaceVectorsForModule({
      projectModuleId: module.id,
      seriesId,
      vectors: nextVectors
    })
  }, [replaceVectorsForModule])

  const handleViewerVectorMutation = useCallback(() => {
    if (isApplyingGroupVisibilityRef.current) return
    if (isEraseGestureActiveRef.current && activeVectorToolRef.current === "erase") {
      hasEraseGestureChangesRef.current = true
      return
    }
    syncVectorsFromViewer()
  }, [syncVectorsFromViewer])

  const handleAnnotationCreated = useCallback(() => {
    handleViewerVectorMutation()
  }, [handleViewerVectorMutation])

  useEffect(() => {
    const source = activeSource
    const canvas = canvasRef.current
    const container = viewerContainerRef.current
    if (!(caseId && canvas && container && source)) return
    const selectedSource = source
    const viewerCanvas = canvas
    const viewerContainer = container

    let cancelled = false
    let initializedViewer: TileViewer | null = null
    let cleanupPointerHandlers: (() => void) | null = null
    let handleAnnotationSelected: ((event: AnnotationSelectedEvent) => void) | null = null
    let statusPointerMove: ((e: PointerMoveEvent) => void) | null = null
    let statusPointerLeave: (() => void) | null = null
    let statusViewportChange: ((e: ViewportChangeEvent) => void) | null = null
    let resizeObserver: ResizeObserver | null = null

    async function initViewer() {
      const headers = await getAuthHeaders()
      if (cancelled) return

      viewerRef.current?.destroy()
      resizeObserver?.disconnect()

      const sourceType: ImageSourceType = selectedSource.sourceType
      // Overlays get their own TileClient cloned from this config with a possibly
      // different sourceType (e.g. a zarr label overlay on a TIFF slide), so every
      // URL pattern for both source types must be present regardless of the base.
      const tileClientConfig = {
        baseUrl: "",
        fetchOptions: headers ? { headers } : undefined,
        sourceType,
        infoUrlPattern: `/api/cases/${caseId}/wsi/sources/{name}/info`,
        tileUrlPattern: `/api/cases/${caseId}/wsi/sources/{name}/tile/{level}/{x}/{y}`,
        thumbnailUrlPattern: `/api/cases/${caseId}/wsi/sources/{name}/thumbnail`,
        zarr: { pathPrefix: `/api/cases/${caseId}/wsi/sources` }
      }

      const viewer = new TileViewer(viewerCanvas, {
        tileClient: tileClientConfig,
        minimap: { enabled: true, anchor: "top-right" },
        annotation: {
          enabled: false,
          defaultStyle: buildVectorStyle("#ef4444"),
          exportPrecision: 1
        },
        sam: {
          proxyPath: "/api/sam",
          fetch: headers
            ? (((url: RequestInfo | URL, init?: RequestInit) =>
                fetch(url, { ...init, headers: { ...init?.headers, ...headers } })) as typeof globalThis.fetch)
            : undefined
        }
      })
      viewerRef.current = viewer
      initializedViewer = viewer

      viewer.on("annotation-created", handleAnnotationCreated)
      viewer.on("annotation-updated", handleViewerVectorMutation)
      viewer.on("annotation-deleted", handleViewerVectorMutation)
      handleAnnotationSelected = (event: AnnotationSelectedEvent) => {
        setSelectedAnnotationId(event.id)
      }
      viewer.on("annotation-selected", handleAnnotationSelected)
      let lastEraserRadius = 0
      let lastDrawRadius = 0
      let lastTool = ""
      viewer.on("annotation-tool-change", (event) => {
        if (event.eraserRadiusPx !== lastEraserRadius) {
          lastEraserRadius = event.eraserRadiusPx
          onVectorEraserRadiusChangeRef.current?.(event.eraserRadiusPx)
        }
        if (event.drawRadiusPx !== lastDrawRadius) {
          lastDrawRadius = event.drawRadiusPx
          if (event.drawRadiusPx > 1) {
            onVectorBrushRadiusChangeRef.current?.(event.drawRadiusPx)
          }
        }
        if (event.tool !== "pan" && event.tool !== "draw" && event.tool !== lastTool) {
          lastTool = event.tool
          onVectorToolChangeRef.current?.(event.tool as VectorTool)
        }
      })

      const handleCanvasMouseDown = (event: MouseEvent) => {
        if (event.button !== 0) return
        if (activeVectorToolRef.current !== "erase") return
        isEraseGestureActiveRef.current = true
        hasEraseGestureChangesRef.current = false
      }
      const handleWindowMouseUp = () => {
        if (!isEraseGestureActiveRef.current) return
        isEraseGestureActiveRef.current = false
        if (!hasEraseGestureChangesRef.current) return
        hasEraseGestureChangesRef.current = false
        syncVectorsFromViewer()
      }
      viewerCanvas.addEventListener("mousedown", handleCanvasMouseDown)
      window.addEventListener("mouseup", handleWindowMouseUp)
      cleanupPointerHandlers = () => {
        viewerCanvas.removeEventListener("mousedown", handleCanvasMouseDown)
        window.removeEventListener("mouseup", handleWindowMouseUp)
      }

      const discovery = new TileClient(tileClientConfig)
      const info = await discovery.fetchImageInfo(selectedSource.id)
      if (cancelled) return

      viewer.loadImage(info)
      viewer.resize()
      setViewerEpoch((v) => v + 1)

      const baseLevel = info.levels[0]!
      const dynamicMaxRadius = Math.round(baseLevel.width * 0.25)
      viewer.setAnnotationMaxDrawRadiusPx(dynamicMaxRadius)
      viewer.setAnnotationMaxEraserRadiusPx(dynamicMaxRadius)
      setViewerStatus({
        pointerImageX: null,
        pointerImageY: null,
        zoom: viewer.getZoom(),
        level: viewer.getCurrentLevel(),
        imageWidth: baseLevel.width,
        imageHeight: baseLevel.height
      })
      statusRef.current = {
        pxX: null,
        pxY: null,
        zoom: viewer.getZoom(),
        level: viewer.getCurrentLevel()
      }

      const scheduleFlush = () => {
        if (!statusRafRef.current) {
          statusRafRef.current = requestAnimationFrame(flushStatus)
        }
      }

      statusPointerMove = (e) => {
        statusRef.current.pxX = e.imageX
        statusRef.current.pxY = e.imageY
        scheduleFlush()
      }
      statusPointerLeave = () => {
        statusRef.current.pxX = null
        statusRef.current.pxY = null
        scheduleFlush()
      }
      statusViewportChange = (e) => {
        statusRef.current.zoom = e.zoom
        statusRef.current.level = e.level
        scheduleFlush()
      }

      viewer.on("pointer-move", statusPointerMove)
      viewer.on("pointer-leave", statusPointerLeave)
      viewer.on("viewport-change", statusViewportChange)

      resizeObserver = new ResizeObserver(() => {
        viewer.resize()
      })
      resizeObserver.observe(viewerContainer)
    }

    void initViewer()

    return () => {
      cancelled = true
      isEraseGestureActiveRef.current = false
      hasEraseGestureChangesRef.current = false
      resizeObserver?.disconnect()
      resizeObserver = null
      cleanupPointerHandlers?.()
      initializedViewer?.off("annotation-created", handleAnnotationCreated)
      initializedViewer?.off("annotation-updated", handleViewerVectorMutation)
      initializedViewer?.off("annotation-deleted", handleViewerVectorMutation)
      if (handleAnnotationSelected) initializedViewer?.off("annotation-selected", handleAnnotationSelected)
      if (statusPointerMove) initializedViewer?.off("pointer-move", statusPointerMove)
      if (statusPointerLeave) initializedViewer?.off("pointer-leave", statusPointerLeave)
      if (statusViewportChange) initializedViewer?.off("viewport-change", statusViewportChange)
      if (statusRafRef.current) cancelAnimationFrame(statusRafRef.current)
      statusRafRef.current = 0
      setViewerStatus(null)
      viewerRef.current?.destroy()
      viewerRef.current = null
    }
  }, [
    activeSource,
    caseId,
    flushStatus,
    getAuthHeaders,
    handleAnnotationCreated,
    handleViewerVectorMutation,
    syncVectorsFromViewer
  ])

  useEffect(() => {
    const viewer = viewerRef.current
    if (!(viewer && activeModule && activeSeriesId)) return

    const isVectorModule = activeModule.template_type === "vector"
    const label = activeVectorLabels.find((item) => item.key === activeVectorLabelKey)
    if (!(isVectorModule && isActiveModuleEditable) || vectorEditingBlocked) {
      viewer.setAnnotationDefaultLabel(null)
      viewer.setAnnotationGroup(null)
      viewer.setAnnotationTool("pan")
      return
    }

    const group = label ? findGroupForLabel(activeVectorGroups, label.key) : null
    viewer.setAnnotationGroup(group?.key ?? null)
    if (activeVectorTool === null) {
      viewer.setAnnotationDefaultLabel(null)
      viewer.setAnnotationTool("pan")
      return
    }

    viewer.setAnnotationEraserRadiusPx(vectorEraserRadiusPx)

    if (activeVectorTool === "sam" || activeVectorTool === "erase") {
      if (label) {
        viewer.setAnnotationDefaultStyle(buildVectorStyle(label.color))
      }
      viewer.setAnnotationDefaultLabel(label?.key ?? null)
      viewer.setAnnotationTool(activeVectorTool)
      return
    }

    if (activeVectorTool === "pen" || activeVectorTool === "brush") {
      if (!label) {
        viewer.setAnnotationDefaultLabel(null)
        viewer.setAnnotationTool("pan")
        return
      }
      viewer.setAnnotationDefaultStyle(buildVectorStyle(label.color))
      viewer.setAnnotationDefaultLabel(label.key)
      viewer.setAnnotationDrawRadiusPx(activeVectorTool === "pen" ? 1 : Math.max(2, vectorBrushRadiusPx))
      viewer.setAnnotationTool("draw")
    }
  }, [
    activeModule,
    activeSeriesId,
    activeVectorLabelKey,
    activeVectorTool,
    vectorEraserRadiusPx,
    vectorBrushRadiusPx,
    activeVectorLabels,
    activeVectorGroups,
    isActiveModuleEditable,
    vectorEditingBlocked
  ])

  // Apply cursor after the tool mapping effect (which resets canvas.style.cursor)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (labelRasterTool && isActiveModuleEditable && activeModule?.template_type === "label_raster") {
      canvas.style.cursor =
        labelRasterTool === "pick" || labelRasterTool === "fill" || labelRasterTool === "erase_fill"
          ? "crosshair"
          : "cell"
      return
    }
    const isVector = activeModule?.template_type === "vector" && isActiveModuleEditable && !vectorEditingBlocked
    if (!isVector) {
      canvas.style.cursor = "grab"
      return
    }
    switch (activeVectorTool) {
      case "pen":
        canvas.style.cursor = "default"
        break
      case "sam":
        canvas.style.cursor = "crosshair"
        break
      case "brush":
      case "erase":
        canvas.style.cursor = "cell"
        break
      default:
        canvas.style.cursor = "grab"
    }
  }, [activeVectorTool, activeModule?.template_type, isActiveModuleEditable, labelRasterTool, vectorEditingBlocked])

  useEffect(() => {
    // Skip re-pushing vectors to the viewer when the change originated from the
    // viewer itself (e.g. user just drew/erased a vector). The viewer already has
    // the correct state; re-pushing triggers expensive earcut triangulation + WebGL
    // re-render for every annotation.
    if (isSyncingFromViewerRef.current) {
      isSyncingFromViewerRef.current = false
      return
    }

    const viewer = viewerRef.current
    if (!(viewer && activeSeriesId && activeModule)) return

    if (activeModule.template_type !== "vector") {
      viewer.clearAnnotations(activeSeriesId)
      return
    }

    const moduleVectors = vectors
      .filter((v) => v.projectModuleId === activeModule.id && v.seriesId === activeSeriesId)
      .map((v) => ({
        ...v.annotation,
        label: v.labelName ?? v.annotation.label,
        imageName: activeSeriesId,
        group: v.labelName ? findGroupForLabel(activeVectorGroups, v.labelName)?.key : undefined
      }))

    if (moduleVectors.length === 0) {
      viewer.clearAnnotations(activeSeriesId)
      return
    }

    viewer.setAnnotations(moduleVectors, "replace")
  }, [activeModule, activeSeriesId, vectors, activeVectorGroups])

  return {
    viewerRef,
    viewerEpoch,
    viewerContainerRef,
    canvasRef,
    viewerStatus,
    activeModuleVectors,
    activeVectorLabelByKey,
    isViewerDropTarget,
    vectorOpacityDraftById,
    selectedAnnotationId,
    handleSelectAnnotation,
    handleVectorVisibilityToggle,
    handleVectorDelete,
    handleVectorOpacityChange,
    handleVectorOpacityCommit,
    hiddenGroupKeys,
    handleToggleGroupVisibility
  }
}
